import React, { useState } from 'react';
import './OptimizedImage.css';

/**
 * ============================================================================
 * PAGE OPTIMIZATION INSTRUCTIONS (FOR DEVELOPERS & AI ASSISTANTS)
 * ============================================================================
 * Whenever asked to "optimize a page using the optimization file", follow these rules:
 * 
 * 1. REPLACE ALL STANDARD <img /> TAGS WITH <OptimizedImage />
 *    - Import this component: `import OptimizedImage from 'path/to/OptimizedImage'`
 *    - Replace `<img src="..." />` with `<OptimizedImage src="..." />`
 * 
 * 2. IDENTIFY ABOVE-THE-FOLD VS BELOW-THE-FOLD
 *    - Any image visible immediately when the page loads (Hero images, logos, top banners):
 *      MUST pass the `priority={true}` prop to <OptimizedImage>. This eagerly loads 
 *      the image and assigns `fetchpriority="high"` to speed up Largest Contentful Paint (LCP).
 *    - Any image that requires scrolling to see (Below-the-fold):
 *      MUST OMIT the `priority` prop. It will default to `loading="lazy"`.
 * 
 * 3. USE PLACEHOLDERS FOR HEAVY IMAGES (LQIP)
 *    - If an image is extremely large (multi-megabyte), generate a tiny base64 
 *      version of it (10px wide) or use a static loading spinner.
 *    - Pass it as `placeholderSrc={tinyBase64String}`. The component will render a 
 *      smooth blur-up effect while the heavy image downloads.
 * 
 * 4. CODE SPLITTING (REACT LAZY)
 *    - Heavily interactive components or modals that are not needed on initial 
 *      page load should be dynamically imported using `React.lazy()` or Loadable Components.
 * ============================================================================
 */


/**
 * OptimizedImage Component
 * A robust wrapper around the standard <img> tag that enforces performance best practices.
 */
const OptimizedImage = ({
  src,
  alt,
  className = '',
  priority = false, // If true, loads immediately. If false, lazy loads.
  placeholderSrc = '', // Optional: Ultra-low-res image or base64 string for blur-up effect
  srcMobile = '', // Optional: tiny mobile-specific replica for bandwidth savings on small screens
  srcSet,
  sizes,
  style = {},
  ...props
}) => {
  const [isLoaded, setIsLoaded] = useState(false);

  // If priority is true, we want to fetch it ASAP and not lazy load it.
  const loadingAttr = priority ? 'eager' : 'lazy';
  const fetchPriorityAttr = priority ? 'high' : 'auto';

  const handleImageLoad = () => {
    setIsLoaded(true);
  };

  return (
    <div 
      className={`optimized-image-wrapper ${className} ${!isLoaded ? 'premium-shimmer-bg' : ''}`} 
      style={{ 
        position: 'relative', 
        overflow: 'hidden', 
        display: 'inline-block', 
        ...style 
      }}
    >
      {/* 
        Placeholder Image (Blur-up logic): 
        Only shown if placeholderSrc is provided and the main high-res image hasn't loaded yet.
      */}
      {placeholderSrc && (
        <img
          src={placeholderSrc}
          alt={`Placeholder for ${alt}`}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: style.objectFit || 'cover',
            filter: 'blur(10px)',
            opacity: isLoaded ? 0 : 1, // Fades out when high-res loads
            transition: 'opacity 0.5s ease-out',
            pointerEvents: 'none'
          }}
          aria-hidden="true"
        />
      )}

      {/* Main High-Resolution Image via CSS Picture tag for extreme performance */}
      <picture style={{ width: '100%', height: '100%', display: 'block' }}>
        {srcMobile && <source media="(max-width: 768px)" type="image/webp" srcSet={srcMobile} />}
        <img
          src={src}
          alt={alt}
          srcSet={srcSet}
          sizes={sizes}
          loading={loadingAttr}
          fetchpriority={fetchPriorityAttr}
          onLoad={handleImageLoad}
          style={{
            width: '100%',
            height: '100%',
            objectFit: style.objectFit || 'cover',
            opacity: isLoaded ? 1 : 0, // Hidden until fully loaded to show skeleton shimmer
            transition: 'opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1)', // Premium smooth fade
          }}
          {...props}
        />
      </picture>
    </div>
  );
};

export default OptimizedImage;
